import { useState, type ReactNode } from 'react';
import { Check, ExternalLink, Rocket } from 'lucide-react';
import { Button, Card, ConfirmDialog } from '@/components/ui';
import { StructureSheet } from '@/components/geo/StructureSheet';
import { useLaunch, useSiteStructure } from '@/hooks/useGeo';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { idempotencyKey } from '@/utils/idempotency';
import { siteUrl } from '@/utils/siteUrl';
import type { SiteDetail } from '@/types/api';

/**
 * What is left of a new GEO before search engines see it (ADR-0047): the
 * structure in the site's language, a look at its pages, and opening it. The
 * first two are advice; the last is the one button.
 */
export function LaunchCard({ site, mayLaunch }: { readonly site: SiteDetail; readonly mayLaunch: boolean }): ReactNode {
  const structure = useSiteStructure(site.id, { enabled: site.templateBuild !== null });
  const [translating, setTranslating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const launch = useLaunch(site.id, {
    onSuccess: () => {
      setConfirming(false);
      toast.success('Сайт открывается для поисковиков');
    },
  });

  const strings = structure.data?.strings ?? [];
  const untranslated = strings.filter((item) => !item.translation).length;
  const pages = structure.data?.pages ?? [];

  return (
    <Card title="Запуск" icon={Rocket}>
      <ol className="launch-steps">
        <li className={untranslated === 0 && strings.length > 0 ? 'launch-step-done' : undefined}>
          <span className="launch-step-mark">{untranslated === 0 && strings.length > 0 ? <Check size={14} aria-hidden="true" /> : '1'}</span>
          <div className="launch-step-body">
            <span className="strong">Перевод структуры</span>
            <span className="muted text-sm">
              {strings.length === 0 ? 'Нечего переводить' : untranslated === 0 ? 'Всё переведено' : `Без перевода: ${untranslated} из ${strings.length}`}
            </span>
          </div>
          {strings.length > 0 ? (
            <Button size="sm" onClick={() => setTranslating(true)}>
              {untranslated === 0 ? 'Изменить' : 'Перевести'}
            </Button>
          ) : null}
        </li>
        <li>
          <span className="launch-step-mark">2</span>
          <div className="launch-step-body">
            <span className="strong">Проверка страниц</span>
            <span className="row-tight row-wrap text-sm">
              {site.primaryDomain ? (
                <a className="link-external" href={siteUrl(site.primaryDomain)} target="_blank" rel="noreferrer noopener">
                  Главная
                  <ExternalLink size={12} aria-hidden="true" />
                </a>
              ) : null}
              {site.primaryDomain
                ? pages.map((page) => (
                    <a key={page.slug} className="link-external" href={siteUrl(site.primaryDomain!, `/${page.slug}/`)} target="_blank" rel="noreferrer noopener">
                      {page.title}
                      <ExternalLink size={12} aria-hidden="true" />
                    </a>
                  ))
                : null}
            </span>
          </div>
        </li>
        <li>
          <span className="launch-step-mark">3</span>
          <div className="launch-step-body">
            <span className="strong">Открыть для поисковиков</span>
            <span className="muted text-sm">Сейчас сайт закрыт от индексации</span>
          </div>
          {mayLaunch ? (
            <Button size="sm" variant="primary" icon={Rocket} onClick={() => setConfirming(true)}>
              Открыть
            </Button>
          ) : null}
        </li>
      </ol>

      <StructureSheet siteId={site.id} open={translating} onClose={() => setTranslating(false)} />
      <ConfirmDialog
        open={confirming}
        title="Открыть сайт для поисковиков?"
        description={untranslated > 0 ? `Без перевода остаётся строк: ${untranslated}. Поисковики увидят их на языке шаблона.` : 'Поисковики начнут индексировать сайт.'}
        confirmLabel="Открыть"
        busy={launch.isPending}
        error={launch.isError ? errorText(launch.error, 'Не удалось открыть сайт') : undefined}
        onConfirm={() => launch.mutate(idempotencyKey('site.launch'))}
        onClose={() => setConfirming(false)}
      />
    </Card>
  );
}
